import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePartnerAuth } from '@/contexts/PartnerAuthContext';
import {
  getPartnerProjects,
  getPartnerProjectDetail,
  getPartnerProjectUpdates,
  submitPartnerNote,
} from '@/services/partnerProjects';

// ===========================================
// Query Keys
// ===========================================

export const partnerProjectKeys = {
  all: ['partner-projects'] as const,
  list: () => [...partnerProjectKeys.all, 'list'] as const,
  detail: (id: string) => [...partnerProjectKeys.all, 'detail', id] as const,
  updates: (id: string) => [...partnerProjectKeys.all, 'updates', id] as const,
};

// ===========================================
// Query Hooks
// ===========================================

/**
 * Fetch all projects for the partner's company
 */
export function usePartnerProjects() {
  const { sessionToken, isAuthenticated } = usePartnerAuth();

  return useQuery({
    queryKey: partnerProjectKeys.list(),
    queryFn: async () => {
      if (!sessionToken) throw new Error('Not authenticated');
      const result = await getPartnerProjects(sessionToken);
      if (!result.success) throw new Error(result.error);
      return result.projects || [];
    },
    enabled: isAuthenticated && !!sessionToken,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch details for a specific project
 */
export function usePartnerProjectDetail(projectId: string) {
  const { sessionToken, isAuthenticated } = usePartnerAuth();

  return useQuery({
    queryKey: partnerProjectKeys.detail(projectId),
    queryFn: async () => {
      if (!sessionToken) throw new Error('Not authenticated');
      const result = await getPartnerProjectDetail(sessionToken, projectId);
      if (!result.success) throw new Error(result.error);
      return result.project;
    },
    enabled: isAuthenticated && !!sessionToken && !!projectId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch updates/notes for a project
 */
export function usePartnerProjectUpdates(projectId: string) {
  const { sessionToken, isAuthenticated } = usePartnerAuth();

  return useQuery({
    queryKey: partnerProjectKeys.updates(projectId),
    queryFn: async () => {
      if (!sessionToken) throw new Error('Not authenticated');
      const result = await getPartnerProjectUpdates(sessionToken, projectId);
      if (!result.success) throw new Error(result.error);
      return result.updates || [];
    },
    enabled: isAuthenticated && !!sessionToken && !!projectId,
    staleTime: 30 * 1000, // 30 seconds for updates (reduced for faster refresh)
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
}

// ===========================================
// Mutation Hooks
// ===========================================

/**
 * Submit a note to a project
 */
export function useSubmitPartnerNote() {
  const { sessionToken } = usePartnerAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      noteContent,
      mentionAssigned = true,
    }: {
      projectId: string;
      noteContent: string;
      mentionAssigned?: boolean;
    }) => {
      if (!sessionToken) throw new Error('Not authenticated');
      const result = await submitPartnerNote(
        sessionToken,
        projectId,
        noteContent,
        mentionAssigned
      );
      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: (_, variables) => {
      // Invalidate updates query to refetch
      queryClient.invalidateQueries({
        queryKey: partnerProjectKeys.updates(variables.projectId),
      });
    },
  });
}
